import type { ReactNode } from "react";

import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import dayjs, { type Dayjs } from "dayjs";

import type { ReferralLinkBaseRecord } from "./ReferralLinksTableProvider";

import { isNoCampaignName } from "../Campaign/isNoCampaignName";
import {
  type LastClickCellTranslations,
  LastClickHeaderCell,
  ReferralLastClickDataCell,
} from "./LastClickCell";
import {
  formatRebateProgram,
  ReferralLinksDataCell,
  ReferralLinksHeaderCell,
  ReferralLinksTooltipText,
  TargetPageColumnCell,
} from "./ReferralLinksTableCells";

export type ReferralLinkColumnTranslations = {
  link: string;
  targetPage: string;
  rebateProgram: string;
  created: string;
  lastClick: string;
  lastClickTooltip: string;
  lastClickCell: LastClickCellTranslations;
  campaign?: string;
  noCampaign?: string;
};

const defaultTooltipFormatter = (date: Dayjs): string =>
  date.format("DD MMM YYYY HH:mm:ss");

const columnHelper = createColumnHelper<ReferralLinkBaseRecord>();

export const referralLinkColumns = <T extends ReferralLinkBaseRecord>(options: {
  translations: ReferralLinkColumnTranslations;
  renderLinkCell: (record: T) => ReactNode;
  formatTooltipDate?: (date: Dayjs) => string;
  getLanguageLabel?: (record: T) => string;
  getCampaignName?: (record: T) => string | undefined;
  showCampaign?: boolean;
  showLastClicked?: boolean;
}): ColumnDef<T, any>[] => {
  const formatTooltipDate =
    options.formatTooltipDate ?? defaultTooltipFormatter;

  const campaignColumn = columnHelper.accessor(
    (row) => options.getCampaignName?.(row as T) ?? "",
    {
      id: "campaign",
      header: () => (
        <ReferralLinksHeaderCell text={options.translations.campaign ?? ""} />
      ),
      cell: (info) => {
        const name = info.getValue();
        return !isNoCampaignName(name) ? (
          <ReferralLinksDataCell
            text={name}
            className="[overflow-wrap:anywhere]"
          />
        ) : (
          <ReferralLinksDataCell
            text={options.translations.noCampaign ?? ""}
            textColor="text-text-disabled"
          />
        );
      },
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 320,
      meta: {
        headerClassName: "w-[320px]",
        cellClassName: "!px-4 !py-[22px] w-[320px]",
      },
    },
  );

  const columns: ColumnDef<ReferralLinkBaseRecord, any>[] = [
    columnHelper.accessor("shortLink", {
      header: () => (
        <ReferralLinksHeaderCell text={options.translations.link} />
      ),
      cell: (info) => options.renderLinkCell(info.row.original as T),
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        headerClassName: "!px-3 !py-1",
        cellClassName: "!p-0 !py-0",
      },
    }),
    columnHelper.accessor("tuneLandingPageName", {
      header: () => (
        <ReferralLinksHeaderCell text={options.translations.targetPage} />
      ),
      cell: (info) => (
        <TargetPageColumnCell
          pageName={info.getValue()}
          language={
            options.getLanguageLabel
              ? options.getLanguageLabel(info.row.original as T)
              : info.row.original.language
          }
        />
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[350px]",
        cellClassName: "!p-0 w-[350px]",
      },
    }),
    ...(options.showCampaign ? [campaignColumn] : []),
    columnHelper.accessor("rebateProgram", {
      header: () => (
        <ReferralLinksHeaderCell text={options.translations.rebateProgram} />
      ),
      cell: (info) => (
        <ReferralLinksDataCell text={formatRebateProgram(info.getValue())} />
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        cellClassName: "!px-4 !py-[22px]",
      },
    }),
    columnHelper.accessor("createdAt", {
      header: () => (
        <ReferralLinksHeaderCell text={options.translations.created} />
      ),
      cell: (info) => {
        const date = dayjs(info.getValue());
        if (!date.isValid()) return <ReferralLinksDataCell text="" />;
        return (
          <ReferralLinksTooltipText
            text={date.format("DD MMM YYYY")}
            tooltipText={formatTooltipDate(date)}
          />
        );
      },
      enableSorting: true,
      sortingFn: "datetime",
      sortDescFirst: true,
      meta: {
        cellClassName: "!px-4 !py-[22px]",
      },
    }),
  ];

  if (options.showLastClicked) {
    columns.push(
      columnHelper.accessor("lastClickedAt", {
        header: () => (
          <LastClickHeaderCell
            title={options.translations.lastClick}
            tooltipText={options.translations.lastClickTooltip}
          />
        ),
        cell: (info) => (
          <ReferralLastClickDataCell
            lastClickedAt={info.getValue()}
            translations={options.translations.lastClickCell}
          />
        ),
        enableSorting: true,
        sortingFn: "datetime",
        sortDescFirst: true,
        meta: {
          cellClassName: "!px-4 !py-[22px]",
        },
      }),
    );
  }

  return columns as unknown as ColumnDef<T, any>[];
};
